export default {
    chat: {
    // Title
        title: 'Chat',
        hiroTitle: 'Hiro',
        hiroSubtitle: 'Drafting Assistant',
        figuresSubtitle: 'Figures Assistant',
        analysisSubtitle: 'Analysis Assistant',
        taskProcessing: 'Task Processing',
        planDefaultTitle: 'Execution Plan',
        planStepFallback: 'Step {index}',
        planTypes: {
            Free: 'Free',
            Internal: 'Internal',
            Pro: 'Pro',
            Enterprise: 'Enterprise',
        },

        // Welcome page
        welcome: {
            title: 'Hello! I\'m Hiro',
        },
        hiroIntro: 'Your drafting assistant, focused on helping you efficiently complete patent application documents.',
        analysisIntro: 'Your patent analysis assistant, helping you break down technical solutions, assess patentability, and strengthen your claims.',
        disclaimer: 'AI-generated for reference only',
        messageResources: {
            template: 'Template',
            figure: 'Figures',
            priorArt: 'Prior art',
            more: '{count} more: {names}',
        },
        readOnlyWarning: 'permission: You have read-only access',

        // Role
        role: {
            user: 'You',
            assistant: 'AI Assistant',
            system: 'System',
        },

        // Status
        status: {
            connected: 'Connected',
            connecting: 'Connecting...',
            disconnected: 'Disconnected',
            error: 'Connection Failed',
        },

        // Backend task status
        taskStatus: {
            PENDING: 'Waiting to execute',
            STARTED: 'Executing',
            SUCCESS: 'Completed',
            FAILURE: 'Execution failed',
            RETRY: 'Retrying',
            REVOKED: 'Cancelled',
            INTERRUPTED: 'Interrupted',
        },

        // Thinking status
        thinking: {
            active: 'Thinking',
            completed: 'Thinking Complete',
        },

        // Agent flow
        agentFlow: {
            completed: 'Completed',
            processing: 'Processing',
            pending: 'Pending',
        },

        // Capabilities
        capabilities: {
            question: 'What can Hiro help with?',
        },

        // Empty state
        empty: {
            tryExamples: 'Quick Start - Try These Examples',
        },

        // Example questions
        examples: [
            'Help me draft an invention patent application for smartphone foldable screen technology',
            'Analyze the examination opinions in this OA notice',
            'Optimize the claim drafting to make it clearer and more complete',
            'Generate a specification based on the technical disclosure',
        ],

        // Figures Agent welcome
        figuresIntro: 'Your patent figures assistant, capable of automatically planning and drawing patent figures based on patent documents.',
        figuresExamples: [
            'Plan patent figures based on the available materials',
            'Generate structural diagrams for the main technical features in the claims',
            'Optimize existing figures to better comply with patent standards',
            'Generate a figures list based on the technical disclosure',
        ],

        // Input box
        input: {
            placeholder: {
                default: 'Type a message... Use {\'@\'} to add references, use / for quick commands',
                disabled: 'Input disabled, please wait...',
                sending: 'Sending message...',
            },
            placeholderDisabled: 'Please wait for Hiro to complete the current task...',
            placeholderWaiting: 'Waiting for response...',
            send: 'Send',
            stop: 'Stop',
            waiting: 'Processing',
            selectedText: 'Selected Text',
            clearSelection: 'Clear Selection',
            disabledFooter: 'Hiro is processing, please wait...',
            keys: {
                instruct: 'Instruct',
                generate: 'Generate',
                edit: 'Edit',
                upload: 'Upload',
                addFile: 'Add file',
                referenceConversationFile: 'Reference conversation files',
            },
            settings: { open: 'More settings' },
            references: {
                remove: 'Remove reference: {name}',
            },
            qaOnly: {
                label: 'Q&A only',
                description: 'Reply in chat without writing to the document',
                tooltip: 'Answers only in chat and never writes to the document',
                busy: 'The current task is running. Q&A-only mode cannot be changed yet.',
                updateFailed: 'Failed to change Q&A-only mode. Please try again.',
                readOnly: 'This project is read-only.',
                executing: 'The current task is running, so this setting cannot be changed.',
                updating: 'Updating Q&A-only mode.',
                permissionLoading: 'Checking project permissions.',
                disabled: 'This setting cannot be changed right now.',
                remove: 'Turn off Q&A only',
            },
            mode: {
                label: 'Mode',
                description: 'Choose how this conversation generates content',
            },
            workMode: {
                agent: 'Agent',
                copilot: 'Copilot',
            },
            dropToUpload: 'Drop files to upload',
            upgrade: 'Upgrade',
        },

        // File upload
        upload: {
            title: 'Upload Files',
            choose: 'Choose files to upload',
            hint: 'Drag and drop files here, or click to browse...',
            uploading: 'Uploading...',
            success: 'Upload successful',
            error: 'Upload failed, please try again',
            successMessage: 'File {fileName} uploaded successfully',
            uploadingMultiple: 'Uploading {total} files...',
            allSuccess: 'Successfully uploaded {count} files',
            partialSuccess: '{success} succeeded, {fail} failed',
            filePromptSuffix: 'Read these files, summarize how their content impacts the current task, and generate next step recommendations.',
        },

        // Chat history
        history: {
            title: 'Chat History',
            empty: 'No chat history',
            search: 'Search conversations',
            rename: 'Rename',
            delete: 'Delete',
            defaultTitle: 'New Chat',
            deleteConfirm: 'Are you sure you want to delete this conversation?',
            inputErrorMessage: 'Please enter a conversation title',
            renameSuccess: 'Renamed successfully',
            // Date group labels
            today: 'Today',
            yesterday: 'Yesterday',
            lastWeek: 'Last 7 Days',
            lastMonth: 'Last 30 Days',
            older: 'Older',
        },

        // Action buttons
        actions: {
            copy: 'Copy',
            copyText: 'Copy as Text',
            copyMarkdown: 'Copy as Markdown',
            copyOptions: 'Copy Options',
            copySuccess: 'Copied to clipboard',
            copyFailed: 'Copy failed',
            noContentToCopy: 'No content to copy',
            exportToWord: 'Export to Word',
            exporting: 'Generating Word document...',
            exportSuccess: 'Word document downloaded',
            exportFailed: 'Export failed, please try again',
            mergeToDocument: 'Merge to Document',
            mergeToDocumentTooltip: 'Merge this content into the current document',
            newChat: 'New Chat',
            selectLanguage: 'Set AI Output Language',
            languageLabel: 'Model Output Language',
            expandFullContent: 'Expand to view full content',
            enterFullscreen: 'Enter Fullscreen',
            exitFullscreen: 'Exit Fullscreen',
        },

        newChatConfirm: {
            message: 'Hiro will start a new chat and save the current chat history in Chat History.',
        },

        // Incomplete tasks reminder dialog
        incompleteTasksDialog: {
            title: 'Tasks Not Yet Complete',
            description: 'There are still incomplete steps in the current task plan:',
            bannerText: 'Task paused — incomplete steps remain.',
            completedLabel: 'Completed',
            incompleteLabel: 'Incomplete',
            continueBtn: 'Continue Tasks',
            endBtn: 'End Tasks',
            continueMessage: 'Please continue executing the remaining task steps',
            endMessage: 'Mark all tasks as completed. Then close the task.',
            inputPlaceholder: 'Additional notes (optional)',
        },

        // Messages
        messages: {
            sendFailed: 'Failed to send message, please try again',
            connecting: 'Connecting to chat...',
            applyingTemplate: 'Applying template...',
            templateAppliedSuccess: 'Successfully applied template: {name}',
            templateApplyFailed: 'Failed to apply template, please try again',
            editorNotInitialized: 'Editor not initialized, unable to apply template',
            modeChanged: 'Work mode changed successfully',
            modeChangeFailed: 'Failed to change work mode',
            modeSwitchNotice: 'Switched from {from} mode to {to} mode',
            modeWillBeSetOnSend: 'Work mode will be applied when you send the first message',
            loadHistoryFailed: 'Failed to load conversation history',
            createFailed: 'Failed to create conversation',
            deleteFailed: 'Failed to delete conversation',
            renameFailed: 'Failed to rename conversation',
            permissionDenied: 'You do not have permission to use {agentName}. Please contact sales to upgrade your plan.',
            permissionDeniedOverseas: 'You do not have permission to use {agentName}. Please {upgradeLink}.',
            upgradePlan: 'upgrade your plan',
            limitExceeded: 'Usage limit exceeded. Please upgrade your plan or try again tomorrow.',
            limitExceededOverseas: 'Usage limit exceeded. Please {upgradeLink} or try again tomorrow.',
            limitExceededGuest: 'Usage limit exceeded. Please sign up or try again tomorrow.',
            taskInProgress: 'Another task is in progress, please wait for it to complete before submitting.',
            globalTaskLimitExceeded: 'System is under high load, please try again later.',
            interruptFailed: 'Failed to interrupt task, please try again',
            contentFilter: 'Input contains sensitive content. Please revise and try again.',
            emptyModelResponse: 'The model did not return valid content. Please try again later.',
        },

        // Composer status bar
        composerStatus: {
            idle: 'Ready for your input…',
            sending: 'Thinking…',
            running: 'Working…',
            runningWithEstimate: 'Working, elapsed {elapsed}, about {estimated} total',
            runningWithElapsed: 'Working, elapsed {elapsed}',
            runningLongTask: 'Working through a multi-step drafting task',
            runningWithRemaining: 'About {remaining} remaining',
            runningFinalizing: 'Finishing up…',
            runningMetaWithEstimate: 'Elapsed {elapsed} · estimate updates as work progresses',
            runningMetaWithElapsed: 'Elapsed {elapsed}',
            interrupting: 'Interrupting…',
        },

        duration: {
            seconds: '{count}s',
            minutes: '{count}m',
            minutesSeconds: '{minutes}m {seconds}s',
        },

        browserNotification: {
            completedTabTitle: 'Hiro task complete',
            failedTabTitle: 'Hiro task failed',
            inputRequiredTabTitle: 'Hiro needs your input',
            completedTitle: 'Hiro task complete',
            completedBody: 'The current drafting task is complete. Return to review the result.',
            failedTitle: 'Hiro task failed',
            failedBody: 'The current drafting task did not complete. Return to review details.',
            inputRequiredTitle: 'Hiro needs your input',
            inputRequiredBody: 'The current task needs your confirmation or additional information to continue.',
        },

        // Agent type names
        agentTypes: {
            disclosure: 'Disclosure',
            prosecution: 'Office Action Response',
            express_prosecution: 'Express Office Action Response',
            patent_draft: 'Patent Drafting',
            unknown: 'this feature',
        },

        // Selected text
        selectedText: {
            label: 'Selected Text:',
            clear: 'Clear selection',
        },

        // Quick actions
        quickActions: {
            draft: {
                title: 'Draft',
                icon: '🔍',
                description: 'Generate patent content and analyze prior art',
                actions: [
                    'Generate claims from disclosure',
                    'Write detailed description',
                    'Find related patents',
                ],
            },
            refine: {
                title: 'Refine',
                icon: '✏️',
                description: 'Strengthen arguments, structure, and polish',
                actions: [
                    'Spot weak arguments and suggest solutions',
                    'Add an executive summary',
                    'Find and fix typos',
                ],
            },
            format: {
                title: 'Format',
                icon: '🎨',
                description: 'Transform your document\'s structure with precision',
                actions: [
                    'Recommend formatting based on content',
                    'Add proper numbering to sections',
                    'Generate table of contents',
                ],
            },
        },

        // Instructions
        instructions: {
            select: 'Select Instruction',
            refresh: 'Refresh instruction list',
            searchPlaceholder: 'Search instructions...',
            noResults: 'No instructions found',
            suggestionsTitle: 'Recommended Instructions',
            useThis: 'Use this instruction',
            navigate: 'Go to Instruction Center',
            instructionContent: 'Instruction Content',
            description: 'Description',
            tags: 'Tags',
            jurisdiction: 'Jurisdiction',
            createdAt: 'Created At',
            exampleOutput: 'Example Output',
            close: 'Close',
        },

        // Pending state
        pending: {
            title: 'Need Your Input',
            subtitle: 'Hiro needs more information to continue',
            inputLabel: 'Your answer',
            waitingInfo: 'Waiting for your confirmation or input',
            tools: {
                askUser: 'Ask User',
                task: 'Task Confirmation',
                todoWrite: 'Todo Item',
            },
        },

        // Decision form
        decisionForm: {
            multiSelectHint: '(Multiple selection)',
            inputPlaceholder: 'Please enter...',
            questionAdditionalPlaceholder: 'If you have any additional notes, please enter here...',
            additionalAnswerLabel: 'Additional',
            submitted: 'Submitted',
            submit: 'Submit',
            cancel: 'Cancel',
            loading: 'Loading decision form...',
            parseError: '⚠️ Decision form data format error',
            upload: {
                button: 'Upload file',
                uploading: 'Uploading...',
                remove: 'Remove {fileName}',
                failed: 'Upload failed, please try again',
                unsupportedType: 'Unsupported file type',
                tooLarge: 'File exceeds 50MB',
            },
        },

        // Instruction form
        instructionForm: {
            kicker: 'Instruction Form',
            fieldCount: '{count} fields',
            instructionContent: 'Instruction Content',
            instructionContentPlaceholder: 'Edit the instruction content to send to Hiro this time',
            inputPlaceholder: 'Please enter...',
            selectPlaceholder: 'Select',
            extraRequirement: 'Additional Requirements',
            extraRequirementPlaceholder: 'Add anything Hiro should consider when using this instruction',
            extraRequirementInline: 'Add specific requirements',
            directRun: 'Run directly',
            charCount: '{count} chars',
            recognizedInstruction: 'Recognized preset instruction "{name}"',
            detail: 'Details',
            requiredError: 'Please fill in "{label}"',
            emptyValue: 'Not provided',
            submit: 'Submit',
            submitted: 'Submitted',
            cancel: 'Cancel',
            cancelled: 'Cancelled',
            parseError: 'Instruction form data format error',
            yes: 'Yes',
            no: 'No',
            outputInstructionName: 'Instruction Name',
            outputInstructionDescription: 'Instruction Description',
            outputInstructionContent: 'Instruction Content',
            outputUserInputs: 'Additional Requirements',
            outputFieldDescription: 'Field Description',
            outputUserAnswer: 'Additional Requirement',
            optionSourceClaims: 'Claims',
            optionSourceFigures: 'Figures',
            optionSourceSelect: 'Select {source}',
            optionSourceReselect: 'Reselect {source} ({count} selected)',
            optionSourceTitle: 'Select {source}',
            optionSourceHint: 'Parse selectable content from the current document. Selected content will be sent to Hiro as additional requirements.',
            optionSourceRefresh: 'Reparse',
            optionSourceLoading: 'Parsing document content...',
            optionSourceEmpty: 'No {source} found',
            optionSourceNoDocument: 'No parsable document content found',
            optionSourceFailed: 'Parsing failed',
            optionSourceRetry: 'Retry',
            optionSourceConfirm: 'Confirm',
        },

        // Template section form
        templateSectionForm: {
            kicker: 'Template Section Form',
            sourceTemplate: 'Source template: {name}',
            sectionPrompt: 'Section Instruction Content',
            sectionPromptPlaceholder: 'Edit the section instruction content to send to Hiro this time',
            extraRequirementPlaceholder: 'Add anything Hiro should consider when generating this section',
            recognizedSection: 'Recognized section instruction "{name}"',
            parseError: 'Template section form data format error',
            outputSectionPrompt: 'Section Instruction Content',
            sectionNames: {
                TITLE: 'Title',
                BACKGROUND: 'Background',
                SUMMARY_OF_INVENTION: 'Summary of Invention',
                BRIEF_DESCRIPTION_OF_FIGURES: 'Brief Description of Figures',
                CLAIMS: 'Claims',
                ABSTRACT: 'Abstract',
                DETAILED_DESCRIPTION: 'Detailed Description',
                DETAILED_DESCRIPTION_PLAN: 'Detailed Description Outline',
                REPEAT_CLAIMS: 'Repeat Claims',
                CUSTOM: 'Custom Section',
            },
        },

        templateSectionPopover: {
            title: 'Template Section Instructions',
            close: 'Close',
            insert: 'Insert into the document',
            apply: 'Apply',
            instruction: 'AI Instruction',
            noInstruction: 'No AI instruction configured',
            fromLibrary: 'From Library',
            remove: 'Delete',
            addSection: 'Add Section',
            newSection: 'New Section',
            empty: 'No template sections are available for the current document',
            chars: 'chars',
            fields: 'fields',
        },

        // Task suggestions
        suggestions: {
            title: 'Task Suggestions',
            agentLabels: {
                novelty: 'Patent Novelty Search Agent',
            },
        },

        // Reference content
        reference: {
            label: 'Referenced selected content',
        },

        // Interrupting
        interrupting: {
            title: 'Stopping Execution',
            description: 'Safely terminating current task...',
            interrupted_by_user: 'Execution interrupted by user',
            processing: 'Processing...',
        },

        // Error messages
        error: {
            technicalDetails: 'Technical Details (Development Only)',
            errorCode: 'Error Code:',
            permissionDeniedReadOnly: 'Read-only permission: cannot send messages',
            permissionDeniedEdit: 'The example project is read-only. Please {createLink} your own editable version.',
            createLink: 'create',
            permissionDeniedDelete: 'Insufficient permission to delete',
            permissionDeniedNoAccess: 'Access denied to this resource',
        },

        // Task list
        tasks: {
            completed: 'Completed',
            markComplete: 'Mark as complete',
            collapse: 'Collapse task list',
            expand: 'Expand to view all tasks',
        },

        // Tool call related
        relatedFiguresResult: {
            title: 'Application and Prior-Art Drawing Evidence', application: 'Source file', applicationDocument: 'Application', loadError: 'The latest drawing evidence could not be loaded. Please try again.',
            issueNavigation: 'Rejection issues', unnamedClaim: 'Claim not specified', examinerReasoning: 'Examiner issue', page: 'PDF page {page}', officialImage: 'Official sheet',
            supportedFeature: 'Disputed feature supported', directObservation: 'Directly visible in the drawing', referenceSigns: 'Reference signs', textualSupport: 'Specification support',
            noTextualSupport: 'No textual support was provided; do not use this as decisive evidence for an amendment or conclusion.', noEvidence: 'The application and cited prior art were checked, but no drawing directly supports this issue.',
            pendingEvidence: 'Drawing evidence extraction is not complete for this issue.', noIssues: 'No rejection issues are available to display.',
            imageLoading: 'Loading image...', imageUnavailable: 'Image is temporarily unavailable', imageRetry: 'Image failed to load. Refresh', retry: 'Retry',
            evidenceBoundary: 'Application, D1, D2, and other sources remain strictly separated. Images alone do not prove exact dimensions, hidden connections, technical effects, or legal conclusions.',
            strategyInvalidated: 'Drawing evidence changed. The prior strategies for {issues} are stale and must be regenerated.',
            status: { completed: 'Completed', pending: 'Pending', failed: 'Failed' },
            summary: { issues: 'Issues checked', officialImages: 'Official sheets checked', supportedIssues: 'Issues with direct support', figures: 'Drawing evidence', noDirectSupport: 'Issues without direct support' },
            confidence: { high: 'High confidence', medium: 'Medium confidence', low: 'Low confidence' },
        },

        toolCall: {
            result: 'View Tool Result',
            toolResultLabel: 'Tool Result',
            toolResultHint: 'This is an intermediate output from tool execution. Please refer to the version in "Documents" for the final content.',
            parameterDetails: 'Parameter Details',
            executing: 'Executing...',
            expand: 'Expand Details',
            collapse: 'Collapse',
            betaBadge: 'Beta',
            spawnSteps: '{count} sub-steps',
        },

        toolGroup: {
            title: '{title} and {count} step{plural}',
            stepCount: '{count} step{plural}',
            completedCount: 'Completed {count}',
            runningCount: 'Running {count}',
            failedCount: 'Failed {count}',
        },

        // Image preview
        imagePreview: {
            generatedImage: 'AI Generated Image',
            zoomIn: 'Zoom In',
            zoomOut: 'Zoom Out',
            download: 'Download',
            close: 'Close',
        },

        // Image generation
        imageGeneration: {
            title: 'Drawing Generation Analysis',
            promptLabel: 'Generated Prompt',
            imagePreview: 'Generated Result',
            noImage: 'No generated image',
            failedTitle: 'Image generation failed',
            timeoutError: 'Image generation timed out. Please try again later.',
            genericError: 'Image generation failed. Please try again later.',
        },

        // Tool names
        tools: {
            unknown: 'Tool Call',
            timeUnknown: '--:--',
            get_current_time: 'Get Current Time',
            create_document: 'Create Document',
            read_document: 'Read Document',
            list_project_attachments: 'List Project Attachments',
            read_attachment_content: 'Read Attachment Content',
            read_image: 'Read Image',
            auto_annotate_image: 'Auto Annotate Image',
            intelligent_patent_search: 'Intelligent Patent Search',
            get_patent_details: 'Get Patent Details',
            paper_semantic_search: 'Paper Semantic Search',
            web_search: 'Web Search',
            patent_drafting_tool: 'Patent Drafting Analysis',
            // Dynamic drafting display name: prefix + section names (collapse to "and N sections" when >2)
            draftingPrefix: 'Draft ',
            draftingMore: ' and {count} sections',
            draftingSections: {
                Claims: 'Claims',
                Title: 'Title',
                Field: 'Technical Field',
                Background: 'Background',
                Summary: 'Summary',
                Drawing_desc: 'Drawings',
                Description: 'Description',
                Abstract: 'Abstract',
                Outline: 'Outline',
            },
            patent_rule_review_tool: 'Patent Rule Review',
            review_oar_response: 'Office Action Response Review',
            extract_oa_and_generate_rejection_table: 'Extract OA Document and Generate Rejection Analysis Table',
            extract_oa_analysis: 'Extract OA Document and Generate Rejection Analysis Table',
            extract_oa_info: 'Extract Office Action Info',
            extract_raw_text: 'Extract Raw Text',
            extract_related_figures: 'Extract Related Figures',
            analyze_oa_rejections: 'Generate Office Action Response Strategy',
            generate_oa_strategy: 'Generate OA Response Strategy',
            generate_oar_content: 'Generate OAR Content',
            render_rejection_table: 'Render Rejection Table',
            update_rejection_table: 'Update Rejection Table',
            validate_oa_strategy: 'Validate OA Response Strategy',
            fetch_office_action_pdf: 'Fetch Office Action PDF',
            fetch_historical_files: 'Fetch Historical Files',
            analyze_disclosure: 'Analyze Disclosure',
            novelty_creativity_evaluation: 'Novelty Creativity Evaluation',
            update_plan: 'Update Plan',
            read_style: 'Read Writing Style',
            read_template: 'Read Template',
            request_user_decision: 'Request User Decision',
            load_skill: 'Load Skill',
            inject_agent_skill: 'Load Historic Context',
            load_skill_content: 'Load Skill',
            detect_and_setup_patent_office: 'Detect and Setup Patent Office',
            write_document: 'Write Document',
            express_generate_reply: 'Generate Reply',
            check_express_eligibility: 'Check Generation Eligibility',
            suggest_next_step: 'Suggest Next Step',
            verify_office_action: 'Verify Office Action',
            analyze_oa_response_strategy: 'Analyze OA Response Strategy',
            streaming: 'Tool outputting...',
            done: 'Tool call completed',
            generate_oar_document: 'Generate OAR Document',
            generate_image: 'Drawing Generation Analysis',
            convert_patent_figure: 'Patent Figure Generation',
            draw_patent_figures: 'Draw Patent Figures',
            label_patent_figures: 'Label Patent Figures',
            plan_patent_drawings: 'Plan Patent Drawings',
            adjust_patent_analysis_step: 'Adjust Analysis Result',
            mask_edit_figure: 'Local Retouch',
            spawn_agent: 'Technical Exploration',
        },

        // Novelty evaluation renderer
        noveltyEvaluation: {
            tabPatents: 'Reference Patents',
            tabEvaluations: 'Evaluation Reports',
            noData: 'No data available',
            parseError: 'Failed to parse data',
        },

        // Patent search result renderer
        patentSearch: {
            found: 'Top {count} related patents',
            searchResult: 'Search Results',
            expand: 'Expand',
            collapse: 'Collapse',
            applicationDate: 'Application Date',
            publicationDate: 'Publication Date',
            relevance: 'Relevance',
            parseError: 'Failed to parse data',
            showDetails: 'Patent Details',
            noData: 'No data available',
            searchTypes: {
                semantic_search: 'Semantic Search',
                structured_query: 'Structured Search',
                patent_number: 'Patent Number Search',
            },
        },

        // Floating window
        floating: {
            minimize: 'Minimize',
            maximize: 'Maximize/Restore',
            close: 'Close',
        },

        // Patent detail renderer
        patentDetail: {
            basicInfo: 'Basic Information',
            patentNumber: 'Patent Number',
            title: 'Title',
            applicationNumber: 'Application Number',
            applicationDate: 'Application Date',
            publicationDate: 'Publication Date',
            inventor: 'Inventor',
            applicant: 'Applicant',
            abstract: 'Abstract',
            copySection: 'Copy Section',
            copyAll: 'Copy All',
            export: 'Export',
            copySuccess: 'Copied {section}',
            copyFailed: 'Copy failed',
            exportSuccess: 'Export successful',
            exportFailed: 'Export failed',
            images: 'Patent Images',
            parseError: 'Failed to parse data',
            fallbackHint: 'Below is the original content:',
            textFormatError: 'Current content is in text format, please view the full content in the conversation.',
        },

        // Paper search result renderer
        paperSearch: {
            title: 'Paper Semantic Search',
            found: 'Found {count} related papers',
            noData: 'No related papers found',
            parseError: 'Failed to parse data',
            showDetails: 'View Details',
            authors: 'Authors',
            journal: 'Journal',
            publishTime: 'Published',
            doi: 'DOI',
            citationCount: 'Citations',
            downloadPdf: 'Download PDF',
            searchModes: {
                text: 'Text Semantic Search',
                paper: 'Similar Paper Search',
                patent: 'Patent Related Papers',
            },
        },

        // Paper detail renderer
        paperDetail: {
            basicInfo: 'Basic Information',
            title: 'Title',
            authors: 'Authors',
            journal: 'Journal',
            publishTime: 'Published',
            doi: 'DOI',
            citationCount: 'Citations',
            referenceCount: 'References',
            organizations: 'Organizations',
            abstract: 'Abstract',
            copyAll: 'Copy All',
            export: 'Export',
            copySuccess: 'Copied {section}',
            copyFailed: 'Copy failed',
            exportSuccess: 'Export successful',
            exportFailed: 'Export failed',
            parseError: 'Failed to parse data',
            fallbackHint: 'Below is the original content:',
        },

        // LLM Replay
        llmReplay: {
            title: 'Request Body',
            formView: 'Form View',
            jsonView: 'JSON View',
            basicConfig: 'Basic Configuration',
            model: 'Model',
            modelPlaceholder: 'e.g. claude-4.5-sonnet',
            temperature: 'Temperature',
            maxTokens: 'Max Tokens',
            stream: 'Stream',
            reasoningEffort: 'Reasoning Effort',
            selectPlaceholder: 'Please select',
            messageList: 'Messages',
            addMessage: 'Add Message',
            messageIndex: 'Message {index}',
            delete: 'Delete',
            role: 'Role',
            rolePlaceholder: 'Select role',
            content: 'Content',
            contentPlaceholder: 'Enter message content',
            jsonPlaceholder: 'Enter request body in JSON format',
            jsonError: 'JSON format error: {message}',
            switchError: 'Cannot switch: JSON format error',
            replayResult: 'Replay Result',
            fieldRole: 'Role',
            fieldContent: 'Content',
            finishReason: 'Finish Reason',
            tokenUsage: 'Token Usage',
            promptTokens: 'Prompt Tokens:',
            completionTokens: 'Completion Tokens:',
            totalTokens: 'Total:',
            modelInfo: 'Model Info',
            modelLabel: 'Model:',
            createdAt: 'Created At:',
            requestId: 'Request ID:',
            statusIdle: 'Idle',
            statusReplaying: 'Replaying',
            statusSuccess: 'Success',
            statusError: 'Failed',
            preserveFieldsHint: 'Only parameters already present are edited here; all other fields are preserved.',
            addUserMessage: 'Add User Message',
            newUserMessage: 'New User Message',
            userMessagePlaceholder: 'Enter the next message; Markdown is supported',
            addToConversation: 'Add to Conversation',
            emptyConversation: 'This request has no messages. Add a User message below.',
            edit: 'Edit',
            save: 'Save',
            cancel: 'Cancel',
            messageJsonError: 'The message must be a JSON object containing a role field',
            additionalFields: 'Additional Fields',
            rawMessage: 'Full Message JSON',
            toolDefinitions: 'Tool Definitions',
            addTool: 'Add Tool',
            rawToolDefinition: 'Full Tool Definition JSON',
            otherParameters: 'Other Request Parameters',
            editOtherInJson: 'These fields are sent unchanged. Switch to JSON view to edit them.',
            requestMustBeObject: 'The request body must be a JSON object',
            reasoningContent: 'Reasoning Content',
            returnedToolCalls: 'Returned Tool Calls',
            invalidToolArguments: 'Arguments are not valid JSON; the raw string is preserved.',
            simulatedToolResult: 'Simulated Tool Result',
            simulatedToolResultPlaceholder: 'Enter the result of this tool call',
            rawToolCall: 'Full Tool Call JSON',
            messagesAdded: 'Messages added to the request context',
            addToolCallAndResults: 'Add Tool Call and Results',
            addAssistantMessage: 'Add Assistant Message',
            completeToolResultsHint: 'Enter every tool result before adding these messages.',
            effectiveRequest: 'Effective Request',
            viewEffectiveRequest: 'View Effective Request Body',
            rawResponse: 'Full Raw Response',
            unrecognizedResponse: 'Unrecognized response shape. Inspect the full raw response.',
            validationFailed: 'Message protocol validation failed',
        },
    },
};
